import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Header } from '../../components';
import {
  mdiFlash,
  mdiCubeOutline,
  mdiCodeTags,
  mdiShieldCheckOutline,
  mdiRocketLaunchOutline,
  mdiAutoFix,
  mdiCellphone,
  mdiWeb,
  mdiServerNetwork,
  mdiCheck,
  mdiArrowRight,
  mdiCreation,
} from '@mdi/js';
import Icon from '@mdi/react';

const DOYO_APP_URL = 'https://app.doyo.ai';

const platformData = [
  { icon: mdiCellphone, name: 'Android', detail: 'Kotlin' },
  { icon: mdiCellphone, name: 'iOS', detail: 'SwiftUI' },
  { icon: mdiWeb, name: 'Web Apps', detail: 'React / Next.js' },
  { icon: mdiServerNetwork, name: 'APIs', detail: 'REST / GraphQL' },
  { icon: mdiServerNetwork, name: 'Backends', detail: 'Node / Python' },
];

const featureIcons = [
  mdiFlash,
  mdiCubeOutline,
  mdiCodeTags,
  mdiShieldCheckOutline,
  mdiRocketLaunchOutline,
  mdiAutoFix,
];

const featureKeys = [
  { title: 'promptToApp', desc: 'promptToAppDesc' },
  { title: 'unlimitedApps', desc: 'unlimitedAppsDesc' },
  { title: 'allModels', desc: 'allModelsDesc' },
  { title: 'yourCode', desc: 'yourCodeDesc' },
  { title: 'instantDeploy', desc: 'instantDeployDesc' },
  { title: 'iterateAI', desc: 'iterateAIDesc' },
];

const planDefs = [
  { id: 'free', price: '$0', period: '', featureCount: 4, popular: false },
  { id: 'bronze', price: '$20', period: '/mo', featureCount: 5, popular: false },
  { id: 'silver', price: '$50', period: '/mo', featureCount: 5, popular: true },
  { id: 'gold', price: '$100', period: '/mo', featureCount: 6, popular: false },
];

function HomePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const [prompt, setPrompt] = useState('');

  if (pathname !== '/') {
    return null;
  }

  const examples = [
    t('home.banner.example1'),
    t('home.banner.example2'),
    t('home.banner.example3'),
  ];

  return (
    <main className="home-new">
      <Helmet
        title={t('seo.titles.home')}
        meta={[
          {
            name: 'description',
            content: t('seo.descriptions.home'),
          },
        ]}
      />
      <Header />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-pattern" />
        <div className="hero-bg-glow" />
        <Container className="hero-content text-center position-relative">
          <div className="hero-badge d-inline-flex align-items-center">
            <Icon path={mdiCreation} size={0.7} />
            <span>{t('home.banner.badge')}</span>
          </div>

          <h1 className="hero-title">
            {t('home.banner.title1')}{' '}
            <span className="text-primary-new">{t('home.banner.title2')}</span>
            <br />
            {t('home.banner.title3')}
          </h1>

          <p className="hero-subtitle">{t('home.banner.subtitle')}</p>

          <div className="hero-prompt-box">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('home.banner.placeholder')}
              rows={3}
              className="hero-textarea"
            />
            <div className="hero-prompt-footer">
              <span className="hero-helper-text">{t('home.banner.helper')}</span>
              <a
                href={`${DOYO_APP_URL}/signup${prompt ? `?prompt=${encodeURIComponent(prompt)}` : ''}`}
                className="btn-primary-new"
              >
                {t('home.banner.button')}
                <Icon path={mdiArrowRight} size={0.7} />
              </a>
            </div>
          </div>

          <div className="hero-examples">
            {examples.map((example) => (
              <button
                key={example}
                onClick={() => setPrompt(example)}
                className="hero-example-btn"
              >
                {example.slice(0, 45)}...
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Platforms Section */}
      <section className="platforms-section">
        <Container>
          <p className="platforms-title">{t('home.platforms.title')}</p>
          <div className="platforms-grid">
            {platformData.map((platform) => (
              <div key={platform.name} className="platform-item">
                <div className="platform-icon">
                  <Icon path={platform.icon} size={1} />
                </div>
                <span className="platform-name">{platform.name}</span>
                <span className="platform-detail">{platform.detail}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">{t('home.features.title')}</h2>
            <p className="section-subtitle">{t('home.features.subtitle')}</p>
          </div>
          <Row>
            {featureKeys.map((fk, i) => (
              <Col md={6} lg={4} key={fk.title} className="mb-4">
                <div className="feature-card">
                  <div className="feature-icon-box">
                    <Icon path={featureIcons[i]} size={0.9} />
                  </div>
                  <h3 className="feature-card-title">{t(`home.features.${fk.title}`)}</h3>
                  <p className="feature-card-desc">{t(`home.features.${fk.desc}`)}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">{t('home.pricing.title')}</h2>
            <p className="section-subtitle">{t('home.pricing.subtitle')}</p>
          </div>
          <Row className="justify-content-center">
            {planDefs.map((plan) => {
              const features = Array.from({ length: plan.featureCount }, (_, i) =>
                t(`home.pricing.${plan.id}.f${i + 1}`)
              );
              return (
                <Col md={6} lg={3} key={plan.id} className="mb-4">
                  <div
                    className={`pricing-card ${plan.popular ? 'pricing-card--popular' : ''}`}
                  >
                    {plan.popular && (
                      <div className="pricing-popular-badge">
                        {t('home.pricing.popular')}
                      </div>
                    )}
                    <div className="pricing-card-header">
                      <h3 className="pricing-plan-name">{plan.id}</h3>
                      <p className="pricing-credits">
                        {t(`home.pricing.${plan.id}.credits`)}
                      </p>
                    </div>
                    <div className="pricing-price">
                      <span className="pricing-amount">{plan.price}</span>
                      {plan.period && (
                        <span className="pricing-period">{plan.period}</span>
                      )}
                    </div>
                    <ul className="pricing-features">
                      {features.map((feature) => (
                        <li key={feature}>
                          <Icon path={mdiCheck} size={0.65} className="pricing-check" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={`${DOYO_APP_URL}/signup`}
                      className={`pricing-cta ${plan.popular ? 'pricing-cta--popular' : ''}`}
                    >
                      {t(`home.pricing.${plan.id}.cta`)}
                    </a>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Tokens Section */}
      <section id="tokens" className="tokens-section">
        <Container>
          <div className="tokens-card">
            <div className="tokens-icon-box">
              <Icon path={mdiFlash} size={1.1} />
            </div>
            <h2 className="section-title">{t('home.tokens.title')}</h2>
            <p className="section-subtitle">{t('home.tokens.subtitle')}</p>

            <div className="tokens-info">
              <div className="tokens-info-item">
                <p className="tokens-info-label">{t('home.tokens.onDemand')}</p>
                <p className="tokens-info-value">{t('home.tokens.from')}</p>
              </div>
              <div className="tokens-info-divider" />
              <div className="tokens-info-item">
                <p className="tokens-info-label">{t('home.tokens.noExpiry')}</p>
                <p className="tokens-info-value">{t('home.tokens.forever')}</p>
              </div>
            </div>

            <a href={`${DOYO_APP_URL}/signup`} className="btn-primary-new">
              {t('home.tokens.cta')}
              <Icon path={mdiArrowRight} size={0.7} />
            </a>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <div className="cta-card">
            <h2 className="cta-title">{t('home.cta.title')}</h2>
            <p className="cta-subtitle">{t('home.cta.subtitle')}</p>
            <a href={`${DOYO_APP_URL}/signup`} className="cta-button">
              {t('home.cta.button')}
              <Icon path={mdiArrowRight} size={0.7} />
            </a>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="new-footer">
        <Container>
          <div className="new-footer-inner">
            <div>
              <a href="#" className="new-footer-logo">
                <span className="text-primary-new">doyo</span>
                <span>.ai</span>
              </a>
              <p className="new-footer-tagline">{t('footer.tagline')}</p>
            </div>
            <div className="new-footer-links">
              <a href="#features">{t('home.features.title').split(' ').slice(0, 2).join(' ')}</a>
              <a href="#pricing">{t('header.pricing')}</a>
              <a href="#tokens">Tokens</a>
            </div>
            <p className="new-footer-copy">
              &copy; {new Date().getFullYear()} Doyo AI. {t('footer.rights')}
            </p>
          </div>
        </Container>
      </footer>
    </main>
  );
}

export default HomePage;
export { HomePage };
